import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../components/ThemeProvider";

const DialogContext = createContext(null);

const queueRef = { list: [] };
let bump = () => {};

/** API imperativa: mismo uso que alert/confirm del navegador, pero con modales del tema. */
export const dialogs = {
  alert(message, options = {}) {
    return new Promise((resolve) => {
      queueRef.list.push({
        kind: "alert",
        message: String(message),
        autoCloseMs: Number(options.autoCloseMs) > 0 ? Number(options.autoCloseMs) : 0,
        resolve,
      });
      bump();
    });
  },
  confirm(message) {
    return new Promise((resolve) => {
      queueRef.list.push({ kind: "confirm", message: String(message), resolve });
      bump();
    });
  },
  prompt(message, options = {}) {
    return new Promise((resolve) => {
      queueRef.list.push({
        kind: "prompt",
        message: String(message),
        inputLabel: options.inputLabel ? String(options.inputLabel) : "Motivo",
        defaultValue: options.defaultValue != null ? String(options.defaultValue) : "",
        required: options.required !== false,
        resolve,
      });
      bump();
    });
  },
};

export function useDialogs() {
  const ctx = useContext(DialogContext);
  if (!ctx) return dialogs;
  return ctx;
}

export function DialogProvider({ children }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [, setVer] = useState(0);
  const [promptText, setPromptText] = useState("");

  useEffect(() => {
    bump = () => setVer((v) => v + 1);
    if (queueRef.list.length) setVer((v) => v + 1);
    return () => {
      bump = () => {};
    };
  }, []);

  const active = queueRef.list[0] || null;

  useEffect(() => {
    if (active?.kind === "prompt") setPromptText(active.defaultValue || "");
  }, [active]);

  const finishAlert = useCallback(() => {
    const item = queueRef.list.shift();
    if (item?.kind === "alert") item.resolve();
    setVer((v) => v + 1);
  }, []);

  const finishConfirm = useCallback((value) => {
    const item = queueRef.list.shift();
    if (item?.kind === "confirm") item.resolve(value);
    setVer((v) => v + 1);
  }, []);

  const finishPrompt = useCallback((value) => {
    const item = queueRef.list.shift();
    if (item?.kind === "prompt") item.resolve(value);
    setVer((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!active || (active.kind !== "confirm" && active.kind !== "prompt")) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (active.kind === "prompt") finishPrompt(null);
        else finishConfirm(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, finishConfirm, finishPrompt]);

  useEffect(() => {
    if (!active || active.kind !== "alert" || !active.autoCloseMs) return;
    const timer = setTimeout(() => finishAlert(), active.autoCloseMs);
    return () => clearTimeout(timer);
  }, [active, finishAlert]);

  const overlay = (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: c.overlayBg || "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={() => {
        if (active?.kind === "alert") finishAlert();
        if (active?.kind === "confirm") finishConfirm(false);
        if (active?.kind === "prompt") finishPrompt(null);
      }}
    >
      <div
        role={active?.kind === "alert" ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby="vcrm-dialog-title"
        style={{
          width: "100%",
          maxWidth: 440,
          background: c.bgPanel,
          border: `1px solid ${c.border}`,
          borderRadius: 14,
          boxShadow: isDark
            ? "0 24px 64px rgba(0,0,0,0.55)"
            : "0 20px 50px rgba(0,0,0,0.18)",
          padding: "22px 24px 18px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="vcrm-dialog-title"
          style={{
            margin: "0 0 12px",
            fontSize: 16,
            fontWeight: 700,
            color: c.textPrimary,
            fontFamily: "inherit",
          }}
        >
          {active?.kind === "prompt" ? "Motivo" : active?.kind === "confirm" ? "Confirmar" : "Aviso"}
        </h2>
        <div
          style={{
            margin: "0 0 20px",
            fontSize: 14,
            lineHeight: 1.5,
            color: c.textSecondary,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "inherit",
          }}
        >
          {active?.message}
        </div>
        {active?.kind === "prompt" && (
          <label style={{ display: "block", margin: "-8px 0 16px" }}>
            {active.inputLabel ? (
              <span style={{ display: "block", fontSize: 11, fontWeight: 700, color: c.textMuted, marginBottom: 6 }}>
                {active.inputLabel}
              </span>
            ) : null}
            <textarea
              autoFocus
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                background: c.bgInput || c.bgCard,
                color: c.textPrimary,
                fontSize: 13,
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
          </label>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
          {(active?.kind === "confirm" || active?.kind === "prompt") && (
            <button
              type="button"
              onClick={() =>
                active?.kind === "prompt" ? finishPrompt(null) : finishConfirm(false)
              }
              style={{
                padding: "9px 18px",
                borderRadius: 9,
                border: `1px solid ${c.border}`,
                background: c.bgCard,
                color: c.textSecondary,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Cancelar
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (active?.kind === "prompt") {
                const v = String(promptText || "").trim();
                if (active.required && !v) return;
                finishPrompt(v);
                return;
              }
              if (active?.kind === "confirm") finishConfirm(true);
              else finishAlert();
            }}
            style={{
              padding: "9px 18px",
              borderRadius: 9,
              border: "none",
              background: active?.kind === "confirm" ? c.accentBlue : c.accentBlue,
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
              minWidth: 100,
            }}
          >
            {active?.kind === "prompt" || active?.kind === "confirm" ? "Aceptar" : "Entendido"}
          </button>
        </div>
      </div>
    </div>
  );

  const value = dialogs;

  return (
    <DialogContext.Provider value={value}>
      {children}
      {active && typeof document !== "undefined"
        ? createPortal(overlay, document.body)
        : null}
    </DialogContext.Provider>
  );
}
